import { useMemo, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  spacing,
  breakpoints,
  palette,
  Typography,
  Modal,
  fontSize,
  fontStyle,
  Progress,
} from '@playdapp/ui';
import { useMedia } from 'react-use';

import { useAppSelector } from 'store/hooks';
import useMoonpay from 'hooks/useMoonpay';
import useTransak from 'hooks/useTransak';
import useOpenControl from 'hooks/useOpenControl';
import useSimplex from 'hooks/useSimplex';
import { chainIdToMainNetworkName } from 'lib/network';
import { getBreakpointQuery, toPascalCase } from 'lib/util';

import type { TokenCurrency } from 'types/wallet';

import MetaTag from './MetaTag';
import Icon from './Icon';
import Image from './Image';

type Props = {
  isTransactionOpen: boolean;
};

type StyleProps = {
  network: string;
  currency: boolean;
};

const ContentBody = styled.div`
  width: 100%;
  min-height: 40vh;
`;

const Title = styled.div`
  position: relative;
  ${fontStyle('b2')};
  margin: 0 auto ${spacing.xl} auto;
  text-align: center;

  ${breakpoints.down('lg')} {
    font-size: ${fontSize.h5};
  }
`;

const CardsGroupArea = styled.div<StyleProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  row-gap: ${spacing.l};
  column-gap: ${({ currency }) => (currency ? 0 : spacing.l)};
  margin-bottom: ${spacing.m};
  border-left: 1px solid ${palette.gray300};

  ${breakpoints.down('lg')} {
    flex-direction: ${(props) => props.network !== 'solana' && 'column'};
  }
`;

const BalanceCardBox = styled.div<{ network: string; currency: boolean }>`
  display: flex;
  justify-content: ${({ network }) =>
    network === 'solana' ? 'flex-start' : 'space-between'};
  align-items: center;
  width: 100%;
  min-height: ${({ theme }) => theme.rem(112)};
  padding: 0 ${spacing.xl};
  margin: ${({ network }) => (network === 'solana' ? '0 ' : 'auto')};
  border: 1px solid ${palette.gray300};
  border-left: none;
  border-radius: ${({ currency }) => (currency ? spacing.m : 0)};
  box-shadow: ${({ currency }) =>
    currency && `0px ${spacing.xxs} ${spacing.xs} 0px #1e1f2426`};
  background-color: ${palette.white};

  ${breakpoints.down('lg')} {
    width: 100%;
    height: 100%;
    padding: ${spacing.m};
    margin-bottom: 0;
    text-transform: uppercase;
  }
`;

const AddFundArea = styled.div`
  padding: ${spacing.xxl};
  margin: ${spacing.xxl} 0;
  border-radius: ${spacing.xl};
  background-color: ${palette.gray200};

  ${breakpoints.down('lg')} {
    display: block;
    text-align: center;
    margin-bottom: 0;
  }
`;

const FundTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 28px;
  gap: ${spacing.l};
  ${fontStyle('b2')};
  font-size: 24px;
  font-weight: 600;

  ${breakpoints.down('sm')} {
    gap: 10px;
    ${fontStyle('b3')};
  }
`;

const BalanceCardText = styled.div<{
  isPlaCurrency: boolean;
  isConnection: boolean;
}>`
  padding-top: ${({ isPlaCurrency, isConnection }) =>
    isPlaCurrency && isConnection && spacing.xl};
  text-align: right;

  > div {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  ${breakpoints.down('lg')} {
    padding: 0;
  }
`;

const SolanaBalanceCardTextBlock = styled.div`
  margin-left: ${spacing.s};

  div:nth-of-type(1) {
    ${fontStyle('p4')};
  }
`;

const AddFundButton = styled.div`
  display: grid;
  justify-items: center;
  row-gap: ${({ theme }) => theme.rem(10)};
  width: 100%;
  min-height: ${({ theme }) => theme.rem(205)};
  max-height: ${({ theme }) => theme.rem(250)};
  padding: ${spacing.xl} ${spacing.l};
  border-radius: ${spacing.m};
  box-shadow: 0px 4px 8px rgba(30, 31, 36, 0.15);
  background-color: ${palette.white};
  text-align: center;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.7;
  }

  ${breakpoints.down('lg')} {
    width: 100%;
    height: auto;
    min-height: auto;
    margin: auto;
    row-gap: ${spacing.m};
    font-weight: 600;
  }
`;

const CardButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50%;
  min-height: ${({ theme }) => theme.rem(84)};
  row-gap: ${spacing.xxs};
  padding: ${spacing.xl} ${spacing.l};
  ${fontStyle('b3')};
  border-radius: 16px;
  box-shadow: 0px ${spacing.xxs} ${spacing.xs} 0px #1e1f2426;
  background-color: ${palette.white};
  text-align: center;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.7;
  }

  ${breakpoints.down('sm')} {
    ${fontStyle('b5')}
  }
`;

const NoticeText = styled.div<StyleProps>`
  max-width: ${({ theme }) => theme.rem(840)};
  margin: ${spacing.xl} auto 0 auto;
  font-weight: 400;
  text-align: center;

  ${(props) =>
    props.network === 'solana'
      ? `${breakpoints.down('sm')} {
    font-size: ${fontSize.p5};
      };`
      : `${breakpoints.down('lg')} {
    padding: ${spacing.xxs};
    margin: ${spacing.l} auto 0 auto;
    font-size: ${fontSize.b4};
    text-align: left;
  };`}
`;

const CardTitle = styled.div`
  font-weight: 600;
`;

const BalanceCount = styled.span`
  ${fontStyle('b1')};
`;

const BalanceLoadingBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    ${fontStyle('b3')};
    margin-right: ${spacing.xs};
  }
`;

const BalanceCurrency = styled.span`
  ${fontStyle('p3')};
  margin-left: ${spacing.xs};
  text-transform: uppercase;
`;

const CurrencyBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  white-space: nowrap;
  gap: ${spacing.xs};
`;

const BridgeText = styled.div`
  margin-bottom: ${({ theme }) => theme.rem(10)};
`;

const CardModalBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CardModalTitle = styled.div`
  text-align: center;
  line-height: 1.5;
`;

const CardModalContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  padding: ${spacing.l};
  margin-top: ${spacing.xl};
  border-radius: ${spacing.m};
  background-color: ${palette.gray200};

  & > span {
    margin-bottom: ${spacing.xl};
  }

  div {
    &:hover {
      opacity: 0.8;
    }

    &:active {
      opacity: 0.8;
    }
  }

  div + div {
    margin-top: ${spacing.m};
  }
`;

const CardModalButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${({ theme }) => theme.rem(48)};
  padding: ${spacing.s} ${spacing.xxl};
  border: 1px solid ${palette.black};
  border-radius: ${spacing.xl};
  background-color: ${palette.white};

  p {
    margin-right: ${spacing.xs};
  }
`;

const ExchangeModalBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ExchangeModalTitle = styled.div`
  text-align: center;
  line-height: 1.5;
`;

const ExchangeModalContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  padding: ${spacing.l};
  margin-top: ${spacing.xl};
  background-color: ${palette.gray200};
  border-radius: ${spacing.m};

  & > p {
    margin-bottom: ${spacing.xl};
  }

  a {
    &:hover {
      opacity: 0.8;
    }

    &:active {
      opacity: 0.8;
    }
  }

  a + a {
    margin-top: ${spacing.m};
  }
`;

const ExchangeModalLink = styled.a`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${({ theme }) => theme.rem(48)};
  padding: ${spacing.s} ${spacing.xxl};
  border: 1px solid ${palette.black};
  border-radius: ${spacing.xl};
  background-color: ${palette.white};

  p {
    margin-right: ${spacing.xs};
  }
`;

const exchangeTarget = {
  ethereum: [
    {
      name: 'Coinbase',
      brand: 'coinbase',
      imageWidth: '79px',
      imageHeight: '14.11px',
      href: 'https://www.coinbase.com/price/playdapp',
    },
    {
      name: 'Binance',
      brand: 'binance',
      imageWidth: '79px',
      imageHeight: '16px',
      href: 'https://www.binance.com/en',
    },
    {
      name: 'Upbit - Korea',
      brand: 'upbit',
      imageWidth: '85px',
      imageHeight: '14px',
      href: 'https://upbit.com/exchange?code=CRIX.UPBIT.KRW-PLA',
    },
    {
      name: 'Kraken',
      brand: 'krake',
      imageWidth: '83px',
      imageHeight: '13.06px',
      href: 'https://www.kraken.com/prices/pla-playdapp-price-chart/usd-us-dollar?interval=1m',
    },
    {
      name: 'Find more exchanges',
      brand: 'marketcap',
      imageWidth: '93px',
      imageHeight: '16px',
      href: 'https://coinmarketcap.com/currencies/playdapp/markets/',
    },
  ],
  polygon: [
    {
      name: 'Binance',
      brand: 'binance',
      imageWidth: '79px',
      imageHeight: '16px',
      href: 'https://www.binance.com/en',
    },
    {
      name: 'Find more exchanges',
      brand: 'marketcap',
      imageWidth: '93px',
      imageHeight: '16px',
      href: 'https://coinmarketcap.com/currencies/playdapp/markets/',
    },
  ],
  solana: [],
  klaytn: [],
};

const MyBalance = ({ isTransactionOpen }: Props) => {
  const [isFirstTransacOpen, setIsFirstTransacOpen] = useState(false);

  const { provider, balance, isBalanceLoading, isBalanceError, activeChainId } =
    useAppSelector(({ wallet }) => wallet);

  const isMobile = useMedia(getBreakpointQuery(breakpoints.down('sm')), false);

  const {
    MoonPayModal,
    isOpen: isOpenMoonpay,
    handleOpen: handleOpenMoonpay,
  } = useMoonpay();
  const {
    SimplexModal,
    isOpen: isOpenSimplex,
    handleOpen: handleOpenSimplex,
  } = useSimplex();
  const { handleOpenTransak } = useTransak();

  const [isCardModalOpen, handleCardModalOpen] = useOpenControl();
  const [isExchangeModalOpen, handleExchangeModalOpen] = useOpenControl();

  const network = useMemo(
    () => chainIdToMainNetworkName[activeChainId || 137],
    [activeChainId],
  );

  const isMdDownScreen = useMedia(getBreakpointQuery(breakpoints.down('md')));

  const nativeCurrencyName = useMemo(
    () => ({
      1: 'eth',
      11155111: 'eth',
      137: 'matic',
      80002: 'matic',
      1001: 'klay',
      8217: 'klay',
    }),
    [],
  );

  const onBuyWithCard = () => {
    handleCardModalOpen(true);
  };

  const onSwap = () => {
    const swapUrl = {
      ethereum: 'https://app.uniswap.org/#/swap',
      polygon: 'https://quickswap.exchange/#/swap',
      klaytn: '',
    };

    if (activeChainId && network !== 'solana')
      window.open(swapUrl[network], '_blank');
  };

  const onTransfer = () => {
    window.open(
      'https://wallet.polygon.technology/login?next=%2Fbridge',
      '_blank',
    );
  };

  const onExchange = () => {
    handleExchangeModalOpen(true);
  };

  useEffect(() => {
    if (isTransactionOpen && !isFirstTransacOpen) {
      setIsFirstTransacOpen(true);
      handleOpenTransak();
    }
  }, [isTransactionOpen, isFirstTransacOpen, handleOpenTransak]);

  return (
    <>
      <MetaTag title="My Balance | PlayDapp" />
      <ContentBody>
        <CardsGroupArea network="" currency>
          {!isBalanceLoading &&
            balance &&
            network !== 'solana' &&
            Object.keys(balance).map((currency) => (
              <BalanceCardBox key={currency} network={network} currency={false}>
                <Icon
                  name={`currency-${currency as TokenCurrency}`}
                  size={48}
                />
                <BalanceCardText
                  isPlaCurrency={currency === 'pla'}
                  isConnection={provider === 'metamask'}
                >
                  <div>
                    <BalanceCount>{balance[currency].toFixed(4)}</BalanceCount>
                    <BalanceCurrency>{currency.toUpperCase()}</BalanceCurrency>
                  </div>
                </BalanceCardText>
              </BalanceCardBox>
            ))}

          {network === 'solana' && (
            <>
              <BalanceCardBox network={network} currency={false}>
                <Icon name="currency-sol" size={48} />
                <SolanaBalanceCardTextBlock>
                  <div>Main Wallet</div>
                  <div>
                    <BalanceCount>
                      {isBalanceLoading && (
                        <BalanceLoadingBox>
                          <span>loading...</span>
                          <Progress size="small" color={palette.primary600} />
                        </BalanceLoadingBox>
                      )}
                      {!isBalanceLoading && isBalanceError && 'Load Failed'}
                      {!isBalanceLoading &&
                        !isBalanceError &&
                        balance?.sol.toLocaleString()}
                    </BalanceCount>
                    {!isBalanceLoading && !isBalanceError && (
                      <BalanceCurrency>SOL</BalanceCurrency>
                    )}
                  </div>
                </SolanaBalanceCardTextBlock>
              </BalanceCardBox>
              <BalanceCardBox network={network} currency={false}>
                <Icon name="currency-sol" size={48} />
                <SolanaBalanceCardTextBlock>
                  <div>Escrow Wallet</div>
                  <div>
                    <BalanceCount>
                      {isBalanceLoading && (
                        <BalanceLoadingBox>
                          <span>loading...</span>
                          <Progress size="small" color={palette.primary600} />
                        </BalanceLoadingBox>
                      )}
                      {!isBalanceLoading && isBalanceError && 'Load Failed'}
                      {!isBalanceLoading &&
                        !isBalanceError &&
                        balance?.escrowSol.toLocaleString()}
                    </BalanceCount>
                    {!isBalanceLoading && !isBalanceError && (
                      <BalanceCurrency>SOL</BalanceCurrency>
                    )}
                  </div>
                </SolanaBalanceCardTextBlock>
              </BalanceCardBox>
            </>
          )}
        </CardsGroupArea>

        {network !== 'solana' && network !== 'klaytn' && (
          <AddFundArea>
            <Title>Add funds</Title>

            <CardsGroupArea network="" currency={false}>
              <AddFundButton role="button" onClick={onBuyWithCard}>
                <Icon name="card" size={48} />
                <CardTitle>Buy with card</CardTitle>
                <CurrencyBox>
                  <Icon name="currency-pla" size={24} />
                  <Icon
                    name={`currency-${
                      nativeCurrencyName[
                        (activeChainId as
                          | 1
                          | 137
                          | 1001
                          | 8217
                          | 80002
                          | 11155111) || 137
                      ] as TokenCurrency
                    }`}
                    size={24}
                  />
                  <Icon name="currency-weth" size={24} />
                </CurrencyBox>
              </AddFundButton>

              <AddFundButton role="button" onClick={onSwap}>
                <Icon name="swap" size={48} />
                <CardTitle>Swap</CardTitle>
                <CurrencyBox>
                  <Icon name="currency-pla" size={24} />
                  <Icon
                    name={`currency-${
                      nativeCurrencyName[
                        (activeChainId as
                          | 1
                          | 137
                          | 1001
                          | 8217
                          | 80002
                          | 11155111) || 137
                      ] as TokenCurrency
                    }`}
                    size={24}
                  />
                  <Icon name="currency-weth" size={24} />
                </CurrencyBox>
              </AddFundButton>

              {(activeChainId === 137 || activeChainId === 80002) && (
                <AddFundButton role="button" onClick={onTransfer}>
                  <Icon name="analyse" size={48} />
                  <div>
                    <CardTitle>Transfer</CardTitle>
                    <BridgeText>via Polygon Bridge</BridgeText>
                    <CurrencyBox>
                      <Icon name="currency-eth" size={24} />
                      <Icon name="currency-matic-dark" size={24} />
                      <Icon name="arrow-right" size={24} />
                      <Icon name="currency-weth" size={24} />
                      <Icon name="currency-matic" size={24} />
                    </CurrencyBox>
                  </div>
                </AddFundButton>
              )}

              <AddFundButton role="button" onClick={onExchange}>
                <Icon name="exchange" size={48} />
                <CardTitle>Buy PLA from Exchange</CardTitle>
                <CurrencyBox>
                  <Icon name="currency-pla" size={24} />
                  <Icon
                    name={`currency-${
                      nativeCurrencyName[
                        (activeChainId as
                          | 1
                          | 137
                          | 1001
                          | 8217
                          | 80002
                          | 11155111) || 137
                      ] as TokenCurrency
                    }`}
                    size={24}
                  />
                </CurrencyBox>
              </AddFundButton>
            </CardsGroupArea>

            <NoticeText network="polygon" currency={false}>
              {network !== 'ethereum' ? (
                <span>
                  You can buy PLA, Matic and WETH with your credit card.
                  <br />
                  <br /> If you already have swap-able assets and wish to
                  exchange them for another one, you can use QuickSwap. <br />
                  <br />
                  If you have assets on a different main network, you can use
                  Polygon Bridge provided by the official Polygon Wallet service
                  to receive your asset to the Polygon network
                </span>
              ) : (
                <span>
                  You can buy PLA and ETH with your credit card.
                  <br />
                  <br /> If you already have swappable assets and wish to
                  exchange them, you can go to Uniswap via swap button.
                </span>
              )}
            </NoticeText>
          </AddFundArea>
        )}

        {network === 'solana' && (
          <AddFundArea>
            <FundTitle>
              <Icon name="card" size={isMobile ? 20 : 40} />
              <span>Buy with card</span>
            </FundTitle>

            <CardsGroupArea network="solana" currency={false}>
              <CardButton
                role="button"
                onClick={() => handleOpenSimplex(!isOpenSimplex)}
              >
                <Icon name="simplex" size={24} />
                <Typography type={isMobile ? 'b5' : 'b3'}>Simplex</Typography>
              </CardButton>

              <CardButton
                role="button"
                onClick={() => handleOpenMoonpay(!isOpenMoonpay)}
              >
                <Icon name="moonpay" size={24} />
                <Typography type={isMobile ? 'b5' : 'b3'}>Moonpay</Typography>
              </CardButton>

              <CardButton role="button" onClick={handleOpenTransak}>
                <Icon name="transak" size={24} />
                <Typography type={isMobile ? 'b5' : 'b3'}>Transak</Typography>
              </CardButton>
            </CardsGroupArea>
          </AddFundArea>
        )}

        <Modal
          isOpen={isCardModalOpen}
          handleOpen={handleCardModalOpen}
          shouldCloseOnOverlayClick
        >
          <CardModalBlock>
            <CardModalTitle>
              <Typography type="h4">Buy with card</Typography>
            </CardModalTitle>
            <CardModalContent>
              <Typography type="b1">Add Funds</Typography>
              <CardModalButton
                onClick={() => handleOpenSimplex(!isOpenSimplex)}
              >
                <Typography type="b3">To Simplex</Typography>
                <Icon
                  name="simplexLogo"
                  width={81}
                  height={39}
                  alt="card-brand"
                />
              </CardModalButton>
              <CardModalButton
                onClick={() => handleOpenMoonpay(!isOpenMoonpay)}
              >
                <Typography type="b3">To Moonpay</Typography>
                <Icon
                  name="moonpayLogo"
                  width={81}
                  height={39}
                  alt="card-brand"
                />
              </CardModalButton>
              <CardModalButton onClick={handleOpenTransak}>
                <Typography type="b3">To Transak</Typography>
                <Icon
                  name="transakLogo"
                  width={81}
                  height={39}
                  alt="card-brand"
                />
              </CardModalButton>
            </CardModalContent>
          </CardModalBlock>
        </Modal>

        <SimplexModal />
        <MoonPayModal />

        <Modal
          isOpen={isExchangeModalOpen}
          handleOpen={handleExchangeModalOpen}
          shouldCloseOnOverlayClick
        >
          <ExchangeModalBlock>
            <ExchangeModalTitle>
              <Typography type="h4">Buy </Typography>
              <Typography type="h4" color="marketplace">
                {toPascalCase(network)} PLA
              </Typography>
              <Typography type="h4"> from Exchange</Typography>
            </ExchangeModalTitle>
            <ExchangeModalContent>
              <Typography type="b1" component="p">
                Add Funds
              </Typography>
              {exchangeTarget[network].map((item) => (
                <ExchangeModalLink
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={item.name}
                >
                  <Typography type={isMdDownScreen ? 'b4' : 'b2'} component="p">
                    To {item.name}
                  </Typography>
                  <Image
                    prefix="s3"
                    src={`/exchange/${item.brand}.png`}
                    width={item.imageWidth}
                    height={item.imageHeight}
                    alt="exchange-brand"
                  />
                </ExchangeModalLink>
              ))}
            </ExchangeModalContent>
          </ExchangeModalBlock>
        </Modal>
      </ContentBody>
    </>
  );
};

export default MyBalance;
